import {
  ButtonSize,
  Carousel,
  IconSearch,
} from '@leland-dev/leland-ui-library';
import { uniqBy } from 'lodash-es';
import Link from 'next/link';
import React, { useMemo, useState } from 'react';

import { type Scalars } from '../../../__generated-gql-types__/globalTypes';
import { initializeApollo } from '../../../lib/apolloClient';
import { addUTMToEventLink, getEventUrl } from '../../../utils/events';
import useMediaQuery, { Breakpoints } from '../../../utils/hooks/useMediaQuery';
import { generateSearchPath } from '../../../utils/search';
import ClassCard from '../../classes/ClassCard';
import { EventCard } from '../../subscriber/EventCard';

import {
  type UpcomingClassesCarouselCategoryFragment,
  type UpcomingClassesCarouselClassFragment,
  UpcomingClassesCarouselSearchClassesAndShindigsDocument,
  type UpcomingClassesCarouselSearchClassesAndShindigsQuery,
  type UpcomingClassesCarouselSearchClassesAndShindigsQueryVariables,
  useUpcomingClassesCarouselSearchClassesAndShindigsQuery,
} from './__generated-gql-types__/UpcomingClassesCarousel.generated';

interface UpcomingClassesCarouselProps {
  title: string;
  categories?: UpcomingClassesCarouselCategoryFragment[];
  checkedOutSessionId?: Scalars['CoachingSessionUrn']['output'];
  onClick?: (c: UpcomingClassesCarouselClassFragment, index: number) => void;
  initialData?: UpcomingClassesCarouselDataProps;
  hideBrowseMore?: boolean;
  trackingPrefix: string;
  freeOnly?: boolean;
  includeFallbackEvents?: boolean;
  showFewerSlides?: boolean;
}

export interface UpcomingClassesCarouselDataProps {
  upcomingClassesAndShindigs?: Possible<UpcomingClassesCarouselSearchClassesAndShindigsQuery>;
}

export const getStaticUpcomingClassesCarouselProps = async ({
  categoryUrn,
}: {
  categoryUrn?: string;
}): Promise<UpcomingClassesCarouselDataProps> => {
  const apolloClient = initializeApollo();
  const { data } = await apolloClient.query<
    Possible<UpcomingClassesCarouselSearchClassesAndShindigsQuery>,
    UpcomingClassesCarouselSearchClassesAndShindigsQueryVariables
  >({
    query: UpcomingClassesCarouselSearchClassesAndShindigsDocument,
    variables: {
      categoryFilter: categoryUrn,
      start: 0,
      count: 4,
    },
  });
  return {
    upcomingClassesAndShindigs: data,
  };
};

const UpcomingClassesCarousel: React.FC<UpcomingClassesCarouselProps> = ({
  title,
  categories,
  checkedOutSessionId,
  onClick,
  initialData,
  hideBrowseMore,
  trackingPrefix,
  freeOnly,
  includeFallbackEvents,
  showFewerSlides,
}) => {
  const isLg = useMediaQuery(Breakpoints.LG);
  const isMd = useMediaQuery(Breakpoints.MD);
  const isSm = useMediaQuery(Breakpoints.SM);

  const [startDate] = useState(Date.now());

  const firstCategory = categories?.[0];

  const { data: clientClassesAndShindigsData } =
    useUpcomingClassesCarouselSearchClassesAndShindigsQuery({
      variables: {
        categoryFilter: categories?.map((category) => category.id),
        freeEvent: freeOnly,
        count: 4,
        start: startDate,
      },
    });

  const { data: clientClassesAndShindigsForGoalsData } =
    useUpcomingClassesCarouselSearchClassesAndShindigsQuery({
      variables: {
        goalFilter: firstCategory?.goal,
        freeEvent: freeOnly,
        count: 4,
        start: startDate,
      },
      skip: !includeFallbackEvents || !firstCategory?.goal,
    });

  const { data: clientClassesAndShindigsForAllData } =
    useUpcomingClassesCarouselSearchClassesAndShindigsQuery({
      variables: {
        freeEvent: freeOnly,
        count: 4,
        start: startDate,
      },
      // if there are no categories, this query would be a repeat of `clientClassesAndShindigsData` so skip it
      skip: !includeFallbackEvents || !categories?.length,
    });

  const upcomingClassesAndShindigsCleaned = useMemo(() => {
    const classesAndShindigsData =
      clientClassesAndShindigsData ||
      clientClassesAndShindigsForGoalsData ||
      clientClassesAndShindigsForAllData
        ? {
            __typename: 'SearchClassesAndShindigs',
            searchClassesAndShindigs: {
              results: uniqBy(
                [
                  ...(clientClassesAndShindigsData?.searchClassesAndShindigs
                    .results ?? []),
                  ...(clientClassesAndShindigsForGoalsData
                    ?.searchClassesAndShindigs.results ?? []),
                  ...(clientClassesAndShindigsForAllData
                    ?.searchClassesAndShindigs.results ?? []),
                ],
                (classOrShindig) =>
                  classOrShindig.__typename === 'Class'
                    ? classOrShindig.classId
                    : classOrShindig.__typename === 'Shindig'
                      ? classOrShindig.shindigId
                      : null,
              ),
            },
          }
        : initialData?.upcomingClassesAndShindigs;

    return classesAndShindigsData?.searchClassesAndShindigs.results.filter(
      (upcomingClassOrShindig) =>
        // keep all shindigs
        upcomingClassOrShindig.__typename !== 'Class' ||
        // filter out classes if no upcoming sessions
        (!!upcomingClassOrShindig.upcomingSessionsV2?.length &&
          // if there is a checked out session, filter out classes for that checked out session
          (!checkedOutSessionId ||
            upcomingClassOrShindig.upcomingSessionsV2?.[0]?.id !==
              checkedOutSessionId) &&
          // filter out classes if the coach has blocked the user
          upcomingClassOrShindig.coach?.coachingRelationship?.blockedByCoach !==
            true),
    );
  }, [
    checkedOutSessionId,
    clientClassesAndShindigsData,
    clientClassesAndShindigsForAllData,
    clientClassesAndShindigsForGoalsData,
    initialData?.upcomingClassesAndShindigs,
  ]);

  const slidesPerView = showFewerSlides
    ? isMd
      ? 3
      : isSm
        ? 2
        : 1.15
    : isLg
      ? 4
      : isSm
        ? 3
        : 1.15;

  return upcomingClassesAndShindigsCleaned?.length ? (
    <Carousel
      title={title}
      buttonSize={ButtonSize.SMALL}
      disableArrowGradient
      swiperProps={{
        slidesPerGroupAuto: false,
        slidesPerView: slidesPerView,
        spaceBetween: 20,
      }}
      sideAction={
        hideBrowseMore
          ? undefined
          : {
              label: `Browse more${isSm ? ' events' : ''}`,
              href: getEventUrl(firstCategory),
              isExternal: false,
            }
      }
      items={
        upcomingClassesAndShindigsCleaned?.map((classOrShindig, index) => {
          if (classOrShindig.__typename === 'Class') {
            const session = classOrShindig.upcomingSessionsV2?.[0];

            return (
              <ClassCard
                key={classOrShindig.classId}
                classInfo={classOrShindig}
                classSession={session}
                onClick={
                  onClick ? () => onClick(classOrShindig, index) : undefined
                }
                trackingPrefix={`${trackingPrefix} - UpcomingClassesCarousel`}
              />
            );
          } else if (classOrShindig.__typename === 'Shindig') {
            return (
              <EventCard
                key={classOrShindig.shindigId}
                event={{
                  ...classOrShindig,
                  link: addUTMToEventLink(classOrShindig.link, 'dashboard'),
                }}
              />
            );
          } else {
            // should not happen, but just in case (and for typing reasons)
            return <></>;
          }
        }) ?? []
      }
    />
  ) : checkedOutSessionId ? (
    <Link
      href={generateSearchPath({
        goal: firstCategory?.goal,
        categorySlug: firstCategory?.slug,
        subCategorySlug: null,
      })}
      className="link link--primary mt-8 flex items-center justify-start whitespace-nowrap text-base font-medium"
      id="upcoming_classes_carousel--browse_more"
    >
      <IconSearch className="mr-1.5 size-5" />
      Browse more{firstCategory?.name ? ` ${firstCategory.name} coaching` : ''}
    </Link>
  ) : null;
};

export default UpcomingClassesCarousel;
